/**
 * Лента ритма видео-референса (спека §12): полоски шириной ∝ длительности сцены, отмеченные — акцентом,
 * `skip` — приглушены, дыры между сценами — пустые. Клик по полоске — плеер на начало сцены, по дыре — на
 * это место; бегунок — текущая позиция плеера. Геометрия — `ribbon` (`lib/videoRef.ts`).
 */
import type { MouseEvent } from "react";

import { fmtDur, fmtTime, rhythmLabel, ribbon, type VideoRefScene } from "@/lib/videoRef";

export function SceneRibbon({
  scenes,
  totalMs,
  currentMs,
  onSeek,
}: {
  scenes: VideoRefScene[];
  totalMs: number;
  currentMs: number;
  onSeek: (ms: number) => void;
}) {
  const bars = ribbon(scenes, totalMs);
  if (bars.length === 0) return null;
  const byId = new Map(scenes.map((s) => [s.id, s]));
  const head = Math.min(100, Math.max(0, (currentMs / totalMs) * 100));

  const seekAt = (e: MouseEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    if (box.width > 0) onSeek(Math.round(((e.clientX - box.left) / box.width) * totalMs));
  };

  return (
    <div className="grid gap-1" data-annot="видео-референс: лента ритма">
      <div className="relative h-7 cursor-pointer rounded-[4px] border border-border bg-bg" onClick={seekAt}>
        {bars.map((b) => {
          const s = byId.get(b.id);
          return (
            <button
              key={b.id}
              type="button"
              title={s ? `сцена ${s.n} · ${fmtTime(s.start_ms)} · ${fmtDur(s.duration_ms)}` : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (s) onSeek(s.start_ms);
              }}
              style={{ left: `${b.left}%`, width: `${b.width}%` }}
              className={
                "absolute inset-y-0.5 rounded-[2px] border-x border-bg " +
                (b.mark === "use" ? "bg-accent/70 hover:bg-accent" : "bg-fg-faint/25 hover:bg-fg-faint/40")
              }
            />
          );
        })}
        <div
          className="pointer-events-none absolute inset-y-0 w-0.5 bg-fg"
          style={{ left: `calc(${head}% - 1px)` }}
          aria-hidden
        />
      </div>
      <div className="text-[11.5px] text-fg-faint tnum">{rhythmLabel(scenes)}</div>
    </div>
  );
}
